/* eslint-disable max-lines */
import type {
  ActivityMessage,
  ChatAppRequest,
  ChatAppResponse,
  ChunkMessage,
  DoneMessage,
  ErrorMessage,
  FileData,
  HeartbeatMessage,
  InitialMessage,
  ServerMessage,
} from "./models"

import { logger } from "@/lib/logger"

import { AuthMode, checkEasyAuth, getAuthMode, refreshEasyAuthToken } from "./auth-api"
import { reacquireCredentialForResume } from "./auth-reconnect"
import { getManagedCredential } from "./auth-refresh-coordinator"

/**
 * Stable reason the backend puts on the error frame it sends when a socket's
 * credential does not authenticate (websocket_decorators.WS_AUTH_EXPIRED_CODE).
 */
const WS_AUTH_EXPIRED_CODE = "auth_expired"

/**
 * Handshake-only: after authenticate_websocket the server does not re-check the
 * user credential per frame. Mid-stream 401s (Azure OpenAI APIStatusError,
 * ERROR_MESSAGE_AUTH) are not recoverable user-credential expiry.
 */
function isAuthExpiredError(errorMsg: ErrorMessage): boolean {
  if (errorMsg.data?.code === WS_AUTH_EXPIRED_CODE) return true
  // Predates the typed `auth_expired` frame (#3072): exact message, no status.
  return errorMsg.status == null && errorMsg.message === "Authentication failed"
}

interface ExtendedWebSocket extends WebSocket {
  onbeforeopening?: () => void
  setRequestHeader?: (name: string, value: string) => void
}

const FLUSH_TUNING = {
  minFlushIntervalMs: 16,
  maxFlushIntervalMs: 42,
  minFlushSize: 96,
  maxFlushSize: 768,
  maxLatencyMs: 42,
  initialFlushSizeThreshold: 320,
}

// The backend sends a heartbeat frame every 20s (websocket_decorators.py,
// heartbeat_interval=20). If we see NO frames at all for this long the TCP
// path is dead (network change, sleep/wake, proxy drop) — the browser never
// fires `close` for these, so without our own watchdog the UI spins forever.
const HEARTBEAT_WATCHDOG_TIMEOUT_MS = 75_000

export interface WebSocketClientOptions {
  url: string
  idToken: string | undefined
  requestData: ChatAppRequest
  files?: File[]
  signal?: AbortSignal
  onHeartbeat?: (timestamp: number) => void
  onOpen?: () => void
  onAuthRefresh?: () => Promise<string | undefined>
  onMessage?: (data: ServerMessage) => void
  onComplete?: () => void
  onConnectionClosed?: () => void
  // Add states for default handling
  setActivityMessage?: (message: string) => void
  setIsLoading?: (loading: boolean) => void
  setHasError?: (hasError: boolean) => void
  setErrorMessage?: (message: string) => void
}

export class WebSocketClient {
  private ws!: ExtendedWebSocket
  private options: WebSocketClientOptions
  private isResolved = false
  private promise: Promise<ChatAppResponse>
  private resolve!: (value: ChatAppResponse) => void
  private reject!: (reason: Error) => void
  private isReconnecting = false
  private streamWriter?: WritableStreamDefaultWriter<Uint8Array>
  private encoder: TextEncoder = new TextEncoder()
  private activityMessages: ActivityMessage[] = []
  // Batching buffer to reduce per-event overhead
  private bufferedContent: string = ""
  private flushTimerId?: number
  private splitFlushRafId?: number
  private splitFlushUsesRaf = true
  private pendingSplitFlushChunks: string[] = []
  private lastFlushAt: number = 0
  // Adaptive buffering with min/max guards
  private minFlushIntervalMs = FLUSH_TUNING.minFlushIntervalMs
  private maxFlushIntervalMs = FLUSH_TUNING.maxFlushIntervalMs
  private minFlushSize = FLUSH_TUNING.minFlushSize
  private maxFlushSize = FLUSH_TUNING.maxFlushSize
  private maxLatencyMs = FLUSH_TUNING.maxLatencyMs
  private currentFlushIntervalMs = FLUSH_TUNING.minFlushIntervalMs
  private currentFlushSizeThreshold = FLUSH_TUNING.initialFlushSizeThreshold
  private splitFlushThresholdChars = 160
  private splitFlushChunkChars = 80
  private splitFlushMaxChunksPerFrame = 2
  private emaInterArrivalMs?: number
  private emaBytesPerSec?: number
  private lastMsgAt?: number
  private heartbeatWatchdogId?: number
  private connectionClosedNotified = false
  // One credential recovery per client. A server that rejects the *fresh*
  // credential too is not something another refresh can fix, and retrying
  // would reconnect-loop against it.
  private authRecoveryAttempted = false
  // authenticate_websocket treats the first frame as `{idToken, request}`.
  // Application sends (HITL decisions) must wait until that payload is written.
  private handshakeSent = false
  private pendingApplicationFrames: Record<string, unknown>[] = []

  constructor(options: WebSocketClientOptions) {
    this.options = options

    this.promise = new Promise<ChatAppResponse>((resolve, reject) => {
      this.resolve = resolve
      this.reject = reject
    })

    this.connect()
  }

  /**
   * Send a control message through the WebSocket connection.
   *
   * Application frames are queued until the handshake `{idToken, request}`
   * payload is on the wire, so an Approve click during silent acquire cannot
   * be consumed as the auth payload.
   */
  public send(message: Record<string, unknown>): void {
    if (this.isClientAbandoned()) {
      throw new Error("WebSocket connection is not open")
    }
    if (!this.handshakeSent) {
      this.pendingApplicationFrames.push(message)
      return
    }
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    } else {
      throw new Error("WebSocket connection is not open")
    }
  }

  private isClientAbandoned(): boolean {
    return this.isResolved || this.options.signal?.aborted === true
  }

  private stopReconnectBecauseAbandoned(): false {
    this.isReconnecting = false
    this.notifyConnectionClosedOnce()
    return false
  }

  private flushPendingApplicationFrames(): void {
    if (!this.handshakeSent || this.ws.readyState !== WebSocket.OPEN) return
    const queued = this.pendingApplicationFrames
    this.pendingApplicationFrames = []
    for (const frame of queued) {
      this.ws.send(JSON.stringify(frame))
    }
  }

  private connect() {
    // The token travels in the first WS message frame as `{ idToken: ... }`
    // (see prepareInitialData below + backend payload-auth path in
    // app/backend/websocket_decorators.py::authenticate_websocket). We do NOT
    // append it to the URL: large Azure AD tokens (with bloated `groups`/`scp`
    // claims) push the GET request line past the websockets library's 8 KB
    // cap and trip `SecurityError: line too long` at handshake, rejecting
    // the upgrade with 400. Keeping the token off the URL also keeps it out
    // of proxy access logs, browser history, and Referer headers.
    if (!this.options.idToken) {
      logger.warn("No authentication token provided for WebSocket connection")
    }

    this.handshakeSent = false
    this.ws = new WebSocket(this.options.url) as ExtendedWebSocket
    this.connectionClosedNotified = false
    this.init()
  }

  /**
   * Invoke onConnectionClosed exactly once per socket lifecycle. Both the
   * heartbeat watchdog and the (possibly much later) close event report the
   * same disconnect — non-idempotent callbacks must not see it twice.
   */
  private notifyConnectionClosedOnce(): void {
    if (this.connectionClosedNotified) return
    this.connectionClosedNotified = true
    if (this.options.onConnectionClosed) {
      this.options.onConnectionClosed()
    }
  }

  public getPromise(): Promise<ChatAppResponse> {
    return this.promise
  }

  private addActivityMessage(activityMsg: ActivityMessage) {
    const translationKey =
      activityMsg.details && typeof activityMsg.details === "object"
        ? (activityMsg.details as Record<string, unknown>).translation_key
        : undefined

    if (translationKey === "reasoning_summary_live") {
      const liveIndex = this.activityMessages.findLastIndex((activity) => {
        const details = activity.details
        return (
          details &&
          typeof details === "object" &&
          (details as Record<string, unknown>).translation_key === "reasoning_summary_live"
        )
      })
      if (liveIndex >= 0) {
        this.activityMessages[liveIndex] = activityMsg
        return
      }
      this.activityMessages.push(activityMsg)
      return
    }

    if (translationKey === "reasoning_summary") {
      this.activityMessages = this.activityMessages.filter((activity) => {
        const details = activity.details
        return !(
          details &&
          typeof details === "object" &&
          (details as Record<string, unknown>).translation_key === "reasoning_summary_live"
        )
      })
    }
    this.activityMessages.push(activityMsg)
  }

  private init() {
    this.ws.onopen = this.onOpen.bind(this)
    this.ws.onmessage = this.onMessage.bind(this)
    this.ws.onerror = this.onError.bind(this)
    this.ws.onclose = this.onClose.bind(this)

    if (this.options.signal) {
      this.options.signal.addEventListener("abort", () => {
        if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
          logger.debug("WebSocketClient: Aborting on signal")
          this.ws.close()
        }
        if (!this.isResolved) {
          this.reject(new DOMException("Aborted", "AbortError"))
        }
        // User-initiated stop: abort (don't cleanly close) the NDJSON stream so
        // the consumer's `for await` loop throws an AbortError instead of ending
        // normally. A clean close here lets handleStreamRequest fall through to
        // finalizeStreamResponse, which commits the partial/empty answer as a
        // completed message, flips canContinue back to false, and never clears
        // isAwaitingSave — leaving the composer locked until the 90s save-guard
        // watchdog fires. Aborting instead routes the consumer to its abort
        // handler, which unlocks the composer and preserves the Continue
        // affordance. Also stops the onClose handler from reporting a lost
        // connection (streamWriter is cleared before ws.close()'s close event).
        //
        // Steer stage-2 stop (issue #2438) deliberately never fires this
        // signal: it sends a {"type":"cancel"} frame via send() and keeps the
        // socket open so the server can wind down and persist the partial
        // (response_complete → message_saved → done). The `done` handler's
        // clean close is the CORRECT terminal for that flow — do not "fix" a
        // cancelled turn's clean close into an abort here.
        this.isResolved = true
        this.stopHeartbeatWatchdog()
        this.abortStream()
      })
    }
  }

  /** True when this socket resumes a run that is already in flight server-side. */
  private isResumingRun(): boolean {
    return (this.options.requestData as { reattach?: unknown } | undefined)?.reattach === true
  }

  /**
   * Make sure the credential this socket is about to present is still good
   * (#3072).
   *
   * Two steps, cheap first. Adopting the credential the app currently manages
   * costs nothing and covers the common case where some other request already
   * refreshed it since this call site captured its copy. Only a *resume* then
   * pays for a silent re-acquisition: it is reconnecting to a run that has been
   * alive long enough for a token to expire, and that run is usually parked in
   * a 300 s wait for this user's plan decision — a rejected resume there is not
   * a retryable blip, it loses the decision and cancels the run.
   *
   * Returns false when the socket must not send: the caller has already been
   * told (stream failed, socket closed).
   */
  private async ensureCredentialForOpen(): Promise<boolean> {
    if (this.isClientAbandoned()) return false
    const managed = getManagedCredential()
    if (managed && this.options.idToken) {
      this.options.idToken = managed
    }
    // `authRecoveryAttempted` means this socket is the reconnect that a
    // just-completed re-acquisition produced — asking for another one would
    // spend a second round trip on a credential we only just proved.
    if (!this.isResumingRun() || this.authRecoveryAttempted) {
      return !this.isClientAbandoned()
    }

    const credential = await reacquireCredentialForResume(this.options.idToken, {
      signal: this.options.signal,
    })
    if (this.isClientAbandoned()) return false
    if (credential.status === "ok") {
      this.options.idToken = credential.token
      return true
    }

    if (credential.status === "reauth-required") {
      // Silent recovery is out and the app's re-login is already under way.
      // Sending the dead token would only spend the run's remaining wait.
      logger.error("Deep resume blocked: credentials expired and could not be renewed silently")
      this.failStream("Your session expired. Sign in again to continue.", {
        code: WS_AUTH_EXPIRED_CODE,
        status: 401,
      })
      this.reject(new Error("WebSocket authentication expired"))
    } else {
      logger.warn("Deep resume abandoned: the auth session changed before it could open")
      this.failStream(
        "The assistant connection ended before the response completed. Please try again.",
        { code: "ws_connection_closed" }
      )
      this.reject(new Error("WebSocket closed before the response completed."))
    }
    this.isResolved = true
    this.stopHeartbeatWatchdog()
    if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
      this.ws.close()
    }
    return false
  }

  private async onOpen() {
    try {
      this.resetHeartbeatWatchdog()
      if (!(await this.ensureCredentialForOpen())) return
      if (this.isClientAbandoned()) return
      // Prepare the data to send (includes both auth token and request data)
      const dataToSend = await this.prepareInitialData()
      if (this.isClientAbandoned()) return

      // Log the data being sent (excluding sensitive information)
      const logData = { ...dataToSend }
      if (logData.idToken) {
        logData.idToken = "***REDACTED***"
      }
      logger.debug("Sending data to WebSocket", logData)

      // Ensure the request property is properly set
      if (!dataToSend.request) {
        logger.error("Missing 'request' property in data to send")
        this.reject(new Error("Missing 'request' property in data to send"))
        this.ws.close()
        return
      }

      // Convert the data to a JSON string
      const dataStr = JSON.stringify(dataToSend)
      logger.debug(`Data size: ${dataStr.length} bytes`)

      // First wire frame after OPEN must be the auth/request payload.
      this.ws.send(dataStr)
      this.handshakeSent = true
      this.flushPendingApplicationFrames()
      // Not application-ready until the handshake is on the wire.
      this.options.onOpen?.()
      logger.debug("Data sent successfully")
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`Error in onOpen: ${errorMessage}`)
      this.reject(error instanceof Error ? error : new Error(String(error)))
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close()
      }
    }
  }

  private async handleAuthError(): Promise<boolean> {
    // Don't attempt to refresh if already reconnecting
    if (this.isReconnecting) {
      return false
    }
    if (this.authRecoveryAttempted) {
      logger.error("WebSocket authentication failed again after a refresh — not retrying")
      return false
    }
    if (this.isClientAbandoned()) {
      return false
    }

    logger.warn("WebSocket authentication error, attempting to refresh token")
    this.isReconnecting = true
    this.authRecoveryAttempted = true

    try {
      // If using custom refresh callback
      if (this.options.onAuthRefresh) {
        const newToken = await this.options.onAuthRefresh()
        if (this.isClientAbandoned()) return this.stopReconnectBecauseAbandoned()
        if (newToken) {
          logger.info("Token refreshed via callback, reconnecting WebSocket")
          this.options.idToken = newToken
          this.reconnect()
          return true
        }
      }
      // If using EasyAuth
      else if (getAuthMode() === AuthMode.EASYAUTH) {
        const refreshed = await refreshEasyAuthToken()
        if (this.isClientAbandoned()) return this.stopReconnectBecauseAbandoned()
        if (refreshed) {
          const stillAuthenticated = await checkEasyAuth()
          if (this.isClientAbandoned()) return this.stopReconnectBecauseAbandoned()
          if (stillAuthenticated) {
            logger.info("EasyAuth token refreshed, reconnecting WebSocket")
            this.reconnect()
            return true
          }
        }
      }

      // Silent re-acquisition through the app's refresh coordinator (#3072).
      // This is the path that matters for a Deep run resuming after a backend
      // restart: the socket carries the token captured when the run started,
      // which on a long run has usually expired. Without this the client sent
      // the dead token, the reconnect was rejected, and the plan decision never
      // reached the run that was waiting for it.
      const credential = await reacquireCredentialForResume(this.options.idToken, {
        signal: this.options.signal,
      })
      if (this.isClientAbandoned()) return this.stopReconnectBecauseAbandoned()
      if (credential.status === "ok") {
        logger.info("WebSocket credential re-acquired silently, reconnecting")
        this.options.idToken = credential.token
        this.reconnect()
        return true
      }
      if (credential.status === "session-changed") {
        logger.warn("Dropping a WebSocket auth failure that crossed an auth session change")
        return false
      }

      // Unrecoverable. The coordinator has already started the single re-login
      // navigation for this credential — calling forceLogout() again here would
      // stack a second redirect and burn the auth-loop guard's burst budget.
      logger.error("WebSocket authentication could not be recovered; re-login under way")
      return false
    } catch (error) {
      logger.error("Error during WebSocket authentication refresh", error as Error)
      return false
    } finally {
      this.isReconnecting = false
    }
  }

  private reconnect() {
    if (this.isClientAbandoned()) {
      this.stopReconnectBecauseAbandoned()
      return
    }
    // Detach the old socket's handlers before closing it: its close event
    // fires in a later macrotask — after handleAuthError's finally block has
    // already cleared isReconnecting — so routing it through onClose would
    // tear down the NDJSON stream and the session-registry binding that this
    // reconnect is meant to preserve. The new socket re-binds handlers in
    // connect() → init().
    const oldWs = this.ws
    oldWs.onopen = null
    oldWs.onmessage = null
    oldWs.onerror = null
    oldWs.onclose = null
    if (oldWs.readyState === WebSocket.OPEN || oldWs.readyState === WebSocket.CONNECTING) {
      oldWs.close()
    }

    // Create a new connection
    this.connect()
  }

  private async prepareInitialData(): Promise<Partial<InitialMessage>> {
    const readFilesAsBase64 = async (fileList: File[]): Promise<FileData[]> => {
      const results: FileData[] = []
      for (const file of fileList) {
        const base64 = await new Promise<string>((res, rej) => {
          const reader = new FileReader()
          reader.onload = () => {
            const result = reader.result as string
            const base64Content = result.split(",")[1]
            res(base64Content)
          }
          reader.onerror = () => {
            rej(new Error(`Failed to read file: ${file.name}`))
          }
          reader.readAsDataURL(file)
        })
        results.push({
          filename: file.name,
          content: base64,
        })
      }
      return results
    }

    const filesData: FileData[] = this.options.files
      ? await readFilesAsBase64(this.options.files)
      : []

    // Ensure we have valid request data
    if (Object.keys(this.options.requestData).length === 0) {
      logger.error("Empty request data provided in options")
      throw new Error("Empty request data provided in options")
    }

    const dataToSend: Partial<InitialMessage> = {
      request: this.options.requestData,
      files: filesData.length > 0 ? filesData : undefined,
    }

    // Always include the token in the payload for authentication
    if (this.options.idToken) {
      dataToSend.idToken = this.options.idToken
      logger.debug("Including authentication token in WebSocket message payload")
    }

    return dataToSend
  }

  private flushBuffer(mode: "normal" | "urgent" = "normal"): void {
    if (mode === "urgent") {
      this.flushPendingSplitChunksNow()
    }

    if (!this.streamWriter || this.bufferedContent.length === 0) return

    const contentToFlush = this.bufferedContent
    this.bufferedContent = ""
    this.lastFlushAt = performance.now()

    const shouldQueueForSplitDrain =
      mode === "normal" &&
      (this.pendingSplitFlushChunks.length > 0 ||
        contentToFlush.length >= this.splitFlushThresholdChars)

    if (shouldQueueForSplitDrain) {
      if (contentToFlush.length >= this.splitFlushThresholdChars) {
        for (let i = 0; i < contentToFlush.length; i += this.splitFlushChunkChars) {
          this.pendingSplitFlushChunks.push(contentToFlush.slice(i, i + this.splitFlushChunkChars))
        }
      } else {
        this.pendingSplitFlushChunks.push(contentToFlush)
      }
      this.scheduleSplitFlushDrain()
      return
    }

    this.writeToStream({ content: contentToFlush })
  }

  private scheduleSplitFlushDrain(): void {
    if (this.splitFlushRafId != null || !this.streamWriter) {
      return
    }

    // Use rAF for visible tabs (smooth rendering), but fall back to setTimeout
    // for background/hidden tabs where rAF is throttled or paused entirely.
    if (document.visibilityState === "visible") {
      this.splitFlushUsesRaf = true
      this.splitFlushRafId = window.requestAnimationFrame(() => {
        this.splitFlushRafId = undefined
        this.drainSplitFlushChunksFrame()
      })
    } else {
      this.splitFlushUsesRaf = false
      this.splitFlushRafId = window.setTimeout(() => {
        this.splitFlushRafId = undefined
        this.drainSplitFlushChunksFrame()
      }, this.currentFlushIntervalMs) as unknown as number
    }
  }

  private drainSplitFlushChunksFrame(): void {
    if (!this.streamWriter) {
      this.pendingSplitFlushChunks = []
      return
    }

    let emitted = 0
    while (emitted < this.splitFlushMaxChunksPerFrame && this.pendingSplitFlushChunks.length > 0) {
      const chunk = this.pendingSplitFlushChunks.shift()
      if (!chunk) {
        break
      }
      this.writeToStream({ content: chunk })
      emitted++
    }

    if (this.pendingSplitFlushChunks.length > 0) {
      this.scheduleSplitFlushDrain()
    }
  }

  private flushPendingSplitChunksNow(): void {
    if (this.splitFlushRafId != null) {
      if (this.splitFlushUsesRaf) {
        window.cancelAnimationFrame(this.splitFlushRafId)
      } else {
        window.clearTimeout(this.splitFlushRafId)
      }
      this.splitFlushRafId = undefined
    }

    if (!this.streamWriter) {
      this.pendingSplitFlushChunks = []
      return
    }

    while (this.pendingSplitFlushChunks.length > 0) {
      const chunk = this.pendingSplitFlushChunks.shift()
      if (!chunk) {
        continue
      }
      this.writeToStream({ content: chunk })
    }
  }

  private clearSplitFlushChunks(): void {
    if (this.splitFlushRafId != null) {
      if (this.splitFlushUsesRaf) {
        window.cancelAnimationFrame(this.splitFlushRafId)
      } else {
        window.clearTimeout(this.splitFlushRafId)
      }
      this.splitFlushRafId = undefined
    }
    this.pendingSplitFlushChunks = []
  }

  private scheduleFlush(intervalMs?: number): void {
    if (this.flushTimerId != null) return
    const interval = intervalMs ?? this.currentFlushIntervalMs
    const elapsed = Math.max(0, performance.now() - this.lastFlushAt)
    const delay = Math.max(0, interval - elapsed)

    this.flushTimerId = window.setTimeout(() => {
      this.flushTimerId = undefined
      this.flushBuffer()
    }, delay)
  }

  private clearFlushTimer(): void {
    if (this.flushTimerId != null) {
      window.clearTimeout(this.flushTimerId)
      this.flushTimerId = undefined
    }
  }

  // Update adaptive parameters based on observed traffic
  private updateAdaptiveParams(contentLen: number): void {
    const now = performance.now()
    if (this.lastMsgAt != null) {
      const gapMs = now - this.lastMsgAt
      const alpha = 0.4
      this.emaInterArrivalMs =
        this.emaInterArrivalMs == null
          ? gapMs
          : alpha * gapMs + (1 - alpha) * this.emaInterArrivalMs
      if (gapMs > 0) {
        const instBytesPerSec = contentLen * (1000 / gapMs)
        this.emaBytesPerSec =
          this.emaBytesPerSec == null
            ? instBytesPerSec
            : alpha * instBytesPerSec + (1 - alpha) * this.emaBytesPerSec
      }
    }
    this.lastMsgAt = now

    // Derive targets with clamps
    const targetInterval =
      this.emaInterArrivalMs != null
        ? Math.min(
            this.maxFlushIntervalMs,
            Math.max(this.minFlushIntervalMs, 0.8 * this.emaInterArrivalMs)
          )
        : this.minFlushIntervalMs

    const estBytesPerSec =
      this.emaBytesPerSec ?? this.currentFlushSizeThreshold * (1000 / this.currentFlushIntervalMs)
    const targetSize = Math.min(
      this.maxFlushSize,
      Math.max(this.minFlushSize, Math.floor((estBytesPerSec * targetInterval) / 1000))
    )

    this.currentFlushIntervalMs = targetInterval | 0
    this.currentFlushSizeThreshold = targetSize | 0
  }

  private onMessage(event: MessageEvent) {
    const dataStr = event.data
    // Any server frame proves the connection is alive
    this.resetHeartbeatWatchdog()
    try {
      const data = JSON.parse(dataStr) as ServerMessage
      // Per-chunk logging disabled for performance

      if (this.options.onMessage) {
        // A consumer callback updates UI state, but it must never be allowed to
        // swallow protocol-critical frames. In particular, losing a `done`
        // frame leaves isResolved false and turns the server's normal close into
        // a misleading "connection lost (code 1000)" error.
        try {
          this.options.onMessage(data)
        } catch (error) {
          logger.error("WebSocketClient onMessage callback failed; continuing protocol handling", {
            error: error instanceof Error ? error.message : String(error),
            messageType: "type" in data ? data.type : "unknown",
          })
        }
      }

      // Handle messages based on type field
      if ("type" in data) {
        switch (data.type) {
          case "chunk": {
            const chunkMsg = data as ChunkMessage
            if (!this.streamWriter) return

            const hasContext = !!chunkMsg.data.context
            const hasFinish = !!chunkMsg.data.finish_reason
            const hasTools = Array.isArray(chunkMsg.data.tool_calls)
            const hasResponseComplete = chunkMsg.data.response_complete === true
            const content = typeof chunkMsg.data.content === "string" ? chunkMsg.data.content : ""

            // Forward control metadata immediately; aggregate content otherwise
            if (hasContext || hasFinish || hasTools || hasResponseComplete) {
              this.flushBuffer("urgent")
              this.handleChunkMessage(chunkMsg)
              return
            }

            if (content.length === 0) {
              return
            }

            // Update adaptive thresholds
            this.updateAdaptiveParams(content.length)

            this.bufferedContent += content
            const timeSinceFlush = performance.now() - this.lastFlushAt
            const sizeReached = this.bufferedContent.length >= this.currentFlushSizeThreshold
            const latencyReached = timeSinceFlush >= this.maxLatencyMs
            const intervalReached = timeSinceFlush >= this.currentFlushIntervalMs

            const shouldFlush = sizeReached || latencyReached || intervalReached
            if (shouldFlush) {
              this.flushBuffer()
            } else {
              this.scheduleFlush(this.currentFlushIntervalMs)
            }
            return
          }

          case "heartbeat": {
            const heartbeat = data as HeartbeatMessage
            // Handle heartbeat
            if (this.options.onHeartbeat) {
              this.options.onHeartbeat(heartbeat.timestamp)
            }
            return
          }

          case "activity": {
            const activityMsg = data as ActivityMessage
            this.addActivityMessage(activityMsg)
            // Surface activities to the stream as they arrive — not just on
            // `done`. Without this, live reasoning summaries (and any other
            // activity) accumulate silently and the UI shows only a spinner
            // during the pre-first-token wait (which can be 25s+ at high
            // effort). `_activities` is a full-snapshot replace on the consumer
            // side, so re-sending the growing list each time is idempotent.
            this.sendActivitiesToStream(this.activityMessages)
            return
          }

          case "queue_status": {
            return
          }

          case "queued": {
            return
          }

          case "processing_started": {
            return
          }

          case "error": {
            const errorMsg = data as ErrorMessage
            this.handleErrorMessage(errorMsg)
            return
          }

          case "done": {
            const done = data as DoneMessage

            // Send accumulated activities over the stream using a special event format.
            // Flush any pending aggregated content before sending activities/completion
            this.flushBuffer("urgent")
            this.sendActivitiesToStream(this.activityMessages)

            // Construct the final response object for the promise resolution.
            const finalResponse = {
              ...done.data,
              activities: this.activityMessages,
            }

            // Handle completion: Resolve the main promise (`this.promise`) returned by getPromise().
            // NOTE: This finalResponse object itself is NOT sent through the NDJSON stream.
            this.stopHeartbeatWatchdog()
            this.resolve(finalResponse)
            this.isResolved = true

            // Close the stream writer if it exists, signaling the end of streamed chunk data.
            if (this.streamWriter) {
              this.closeStream()
            }

            // Call the optional onComplete callback provided in options.
            if (this.options.onComplete) {
              this.options.onComplete()
            }
            return
          }
        }
      }

      logger.debug("Received other WebSocket message type", data)
    } catch (error) {
      logger.error(
        "Failed to parse WebSocket message (message-level error, not a connection error):",
        {
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        }
      )
    }
  }

  /**
   * Process a chunk message based on its format
   */
  private handleChunkMessage(chunkMsg: ChunkMessage): void {
    if (!this.streamWriter) return

    this.writeCustomChunkToStream(chunkMsg.data)
  }

  /**
   * Writes a custom format chunk to the stream
   */
  private writeCustomChunkToStream(data: {
    content?: string
    tool_calls?: unknown[]
    finish_reason?: string
    response_complete?: boolean
    context?: unknown
  }): void {
    if (!this.streamWriter) return

    try {
      // Pass through our custom format directly
      this.writeToStream(data)
    } catch (error) {
      logger.error(
        "Error writing custom chunk to stream",
        error instanceof Error ? error : new Error(String(error))
      )
    }
  }

  /**
   * Send activities to the stream handler via a special event
   */
  private sendActivitiesToStream(activities: ActivityMessage[]): void {
    // Only send if we're streaming and there are activities to send
    if (!this.streamWriter || activities.length === 0) return

    // Create a special activities event object (using a non-standard key like _activities)
    const activitiesEvent = {
      _activities: activities,
    }

    // Write the activities object to the stream as a separate NDJSON line
    this.writeToStream(activitiesEvent)
  }

  /**
   * Generic method to write any object to the stream
   */
  private writeToStream(data: Record<string, unknown>): void {
    if (!this.streamWriter) return

    try {
      void this.streamWriter.write(this.encoder.encode(JSON.stringify(data) + "\n"))
    } catch (error) {
      logger.error(
        "Error writing to stream",
        error instanceof Error ? error : new Error(String(error))
      )
    }
  }

  /**
   * Terminate the NDJSON stream with a typed error frame so consumers
   * (`for await … readNDJSONStream`) surface an error + retry instead of
   * awaiting forever. Safe to call when no stream is active.
   */
  private failStream(message: string, extra?: Record<string, unknown>): void {
    if (!this.streamWriter) return
    try {
      this.writeToStream({ error: message, type: "error", ...extra })
    } catch {
      // ignore — closeStream below still terminates the consumer loop
    } finally {
      this.closeStream()
    }
  }

  /**
   * (Re)arm the dead-socket watchdog. Any frame from the server (heartbeat,
   * chunk, activity, …) proves liveness and resets it.
   */
  private resetHeartbeatWatchdog(): void {
    this.stopHeartbeatWatchdog()
    this.heartbeatWatchdogId = window.setTimeout(() => {
      this.heartbeatWatchdogId = undefined
      this.onHeartbeatWatchdogExpired()
    }, HEARTBEAT_WATCHDOG_TIMEOUT_MS)
  }

  private stopHeartbeatWatchdog(): void {
    if (this.heartbeatWatchdogId != null) {
      window.clearTimeout(this.heartbeatWatchdogId)
      this.heartbeatWatchdogId = undefined
    }
  }

  private onHeartbeatWatchdogExpired(): void {
    logger.error(
      `WebSocket heartbeat watchdog expired — no frames for ${HEARTBEAT_WATCHDOG_TIMEOUT_MS / 1000}s, treating connection as dead`
    )
    this.failStream(
      "Connection to the assistant was lost (no response from server). Please try again.",
      { code: "client_heartbeat_timeout" }
    )
    if (!this.isResolved) {
      this.reject(new Error("WebSocket connection timed out (heartbeat watchdog)"))
      this.isResolved = true
    }
    // Close the socket; on a dead TCP path the close event may be delayed or
    // never arrive, which is exactly why the stream is failed above first.
    try {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close()
      }
    } catch {
      // ignore
    }
    this.notifyConnectionClosedOnce()
  }

  /**
   * Abort the NDJSON stream with an AbortError. On a user-initiated stop the
   * consumer's `for await … readNDJSONStream` loop then throws an AbortError
   * (rather than ending cleanly), so handleStreamRequest routes to its abort
   * handler — which unlocks the composer (clears isAwaitingSave), preserves the
   * Continue affordance (leaves canContinue as handleCancelRequest set it), and
   * does NOT commit the partial/empty answer as a completed message. A clean
   * close instead runs finalizeStreamResponse, leaving the send box disabled
   * until the 90s save-guard watchdog fires. Safe to call with no active stream.
   */
  private abortStream(): void {
    if (!this.streamWriter) return
    // Clear pending flush work so no post-abort callbacks fire
    this.clearFlushTimer()
    this.clearSplitFlushChunks()
    const writer = this.streamWriter
    this.streamWriter = undefined
    this.bufferedContent = ""
    try {
      // Aborting the writable side errors the TransformStream's readable side
      // with this reason; the consumer's read() rejects with it, so the
      // for-await throws a DOMException whose name is "AbortError".
      void writer.abort(new DOMException("Aborted", "AbortError"))
    } catch {
      // ignore — the consumer loop still terminates via the errored stream
    }
  }

  /**
   * Closes the stream
   */
  private closeStream(): void {
    if (this.streamWriter) {
      // Ensure buffered content is flushed before closing
      this.flushBuffer("urgent")
      // Clear any pending flush timer to avoid post-close callbacks
      this.clearFlushTimer()
      this.clearSplitFlushChunks()
      void this.streamWriter.close()
      this.streamWriter = undefined
      // Reset buffer to release memory
      this.bufferedContent = ""
    }
  }

  private async onError(event: Event) {
    // This is a connection-level error
    logger.error("WebSocket connection error occurred:", {
      eventType: event.type,
      errorMessage: event instanceof ErrorEvent ? event.message : "Unknown error",
      timestamp: new Date().toISOString(),
    })

    // Prevent any further scheduled flushes after an error
    this.clearFlushTimer()
    this.clearSplitFlushChunks()
    this.bufferedContent = ""

    if (!this.isResolved) {
      // Check if this is likely an authentication error
      if (
        event instanceof ErrorEvent &&
        (event.message.includes("401") ||
          event.message.includes("auth") ||
          event.message.includes("token"))
      ) {
        logger.warn("WebSocket error appears to be authentication-related")

        // Try to refresh the token and reconnect
        const refreshed = await this.handleAuthError()
        if (!refreshed) {
          // Only reject if refresh failed
          this.reject(new Error("Authentication failed: WebSocket connection rejected"))
        }
      } else {
        this.reject(new Error("WebSocket connection error occurred."))
      }
    }
  }

  private onClose(event: CloseEvent) {
    // Closed
    // Clear any pending flushes and buffered content when the socket closes
    this.clearFlushTimer()
    this.clearSplitFlushChunks()
    this.bufferedContent = ""
    this.stopHeartbeatWatchdog()

    if (this.isReconnecting) {
      if (!this.isClientAbandoned()) {
        // Token-refresh reconnect in flight: the new socket will resume writing
        // into the same NDJSON stream. Don't fail the stream, don't reject, and
        // keep the session-registry binding (onConnectionClosed unregisters it,
        // which would leave tool approvals pointing at nothing after reconnect).
        return
      }
      // Abort won over reconnect: the close would otherwise skip unregister.
      this.isReconnecting = false
    }

    if (!this.isResolved) {
      // A close code only describes the transport. Completion is successful
      // only after this client has processed the application's `done` frame.
      // Treating 1000 as success here leaves non-streaming callers awaiting a
      // promise forever when the server closes before delivery of that frame.
      this.reject(new Error("WebSocket closed before the response completed."))
    }

    // Terminate an active NDJSON stream. Without this, an abnormal close mid-
    // stream (network blip, backend restart, proxy timeout) leaves the consumer
    // `for await` loop waiting forever — UI frozen in "Thinking…" with no error.
    // Note the rejection above is unobserved on the streaming path (the caller
    // returns createStreamingResponse() without awaiting getPromise()), so the
    // stream is the only channel that reaches the user.
    if (this.streamWriter) {
      if (this.isResolved) {
        this.closeStream()
      } else {
        this.failStream(
          "The assistant connection ended before the response completed. Please try again.",
          { code: "ws_connection_closed", close_code: event.code, close_reason: event.reason }
        )
        this.isResolved = true
      }
    }

    // Call onConnectionClosed callback if provided (once per socket lifecycle)
    this.notifyConnectionClosedOnce()
  }

  /**
   * Creates a streaming Response object from this WebSocket connection
   * @returns A Response object with a readable stream
   */
  public createStreamingResponse(): Response {
    // Create a TransformStream for streaming data
    const { readable, writable } = new TransformStream()
    this.streamWriter = writable.getWriter()

    // On the streaming path errors are delivered through the NDJSON stream
    // (typed error frames), not via this.promise — callers never await it.
    // Observe the rejection so a disconnect doesn't surface as an unhandled
    // promise rejection (console noise + global error handlers).
    void this.promise.catch(() => {})

    // Return a Response with the stream
    return new Response(readable, {
      headers: {
        "Content-Type": "application/x-ndjson",
      },
    })
  }

  /**
   * Recover from a server error frame that says our credential expired.
   *
   * `handleAuthError()` sets `isReconnecting` before its first await, so the
   * close the server sends right behind the error frame is absorbed by
   * `onClose` instead of tearing down the NDJSON stream this resume writes into.
   */
  private async recoverFromAuthErrorFrame(): Promise<void> {
    this.stopHeartbeatWatchdog()
    if (await this.handleAuthError()) return

    this.failStream("Your session expired. Sign in again to continue.", {
      code: WS_AUTH_EXPIRED_CODE,
      status: 401,
    })
    if (!this.isResolved) {
      this.reject(new Error("WebSocket authentication expired"))
      this.isResolved = true
    }
  }

  /**
   * Handle an error message from the server
   */
  private handleErrorMessage(errorMsg: ErrorMessage): void {
    // An expired credential is recoverable, and it is the one server error we
    // must not turn into a dead stream: the socket it arrives on is usually a
    // resume of a run that is parked waiting for this user's plan decision
    // (#3072). Refresh silently and reconnect; only if that fails does the
    // stream end, and then with a reason the user can act on.
    if (!this.isResolved && isAuthExpiredError(errorMsg)) {
      void this.recoverFromAuthErrorFrame()
      return
    }

    const errorObj = new Error(errorMsg.message)
    this.stopHeartbeatWatchdog()

    // For streaming cases, write the error to the stream before closing
    if (this.streamWriter) {
      try {
        // Write the error as a properly formatted JSON object to the stream.
        // Forward `data` (code/error_id from e.g. the backend stall watchdog)
        // so the reference id the user is asked to quote actually survives
        // the WS → NDJSON hop.
        this.writeToStream({
          error: errorMsg.message,
          status: errorMsg.status,
          error_id: errorMsg.error_id,
          data: errorMsg.data,
          type: "error",
        } as Record<string, unknown>)
      } catch {
        // ignore
      } finally {
        this.closeStream()
      }
    }

    this.reject(errorObj)
    this.isResolved = true
  }
}
